import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SelectWithAlert } from "@/components/ui/select-with-alert";
import { GTUData, UNIT_VARIANTS, vatData, forbiddenVatType } from "@/constants";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const formatNumber = (value) => {
  const num = parseFloat(value) || 0;
  return num.toFixed(2);
};

const DiffCell = ({ value }) => {
  const num = parseFloat(value) || 0;
  const isPositive = num > 0;
  const isNegative = num < 0;

  return (
    <span className={`font-medium ${isPositive ? 'text-green-600' : ''} ${isNegative ? 'text-red-600' : ''}`}>
      {isPositive ? '+' : ''}{formatNumber(value)}
    </span>
  );
};

export const CorrectionPositionsSection = ({ form, inputChecked }) => {
  const { t } = useTranslation();

  const {
    correctedItems,
    handleItemChange,
    addNewItem,
    removeItem,
  } = form;

  const hasNoChanges = inputChecked && !correctedItems.some(item =>
    item.diffAmount !== 0 ||
    item.diffPriceNetto !== 0 ||
    item.isNew
  );

  const gtuOptions = GTUData.map(g => ({ value: g, label: g }));
  const unitOptions = UNIT_VARIANTS.map(u => ({ value: u, label: u }));
  const vatOptions = vatData
    .filter(v => !forbiddenVatType.includes(v))
    .map(v => ({ value: v, label: v }));

  return (
    <div className="w-full pb-6">
      <div className="flex justify-between items-center pb-6">
        <div className="text-xl font-medium">
          {t("invoices:correction_positions")}
        </div>
        <Button
          variant="outline"
          onClick={addNewItem}
          className="text-blueText"
        >
          <AddIcon fontSize="small" className="mr-1" />
          {t("invoices:button_add_position")}
        </Button>
      </div>

      {hasNoChanges && (
        <div className="text-red-500 text-sm mb-4">
          {t("invoices:no_corrections")}
        </div>
      )}

      <div className="space-y-4">
        {correctedItems.map((item, index) => (
          <div key={item.id || `new-${index}`} className="bg-white rounded-md border p-4">
            {/* Row 1: Name (full width) */}
            <div className="mb-4">
              <label className="block text-sm font-medium pb-1">
                {t("invoices:positions_name")}
              </label>
              <Textarea
                value={item.afterName}
                onChange={(e) => handleItemChange(index, 'afterName', e.target.value)}
                placeholder={t("invoices:positions_name")}
                className="w-full min-h-[60px] bg-white"
              />
              {!item.isNew && item.beforeName !== item.afterName && (
                <div className="text-xs text-gray-500 mt-1">
                  {t("invoices:before_value")}: {item.beforeName}
                </div>
              )}
            </div>

            {/* Row 2: GTU, PKWIU (conditional), Amount, Unit, Netto, VAT */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              {/* GTU */}
              <div>
                <SelectWithAlert
                  label="GTU"
                  value={item.afterGTU}
                  options={gtuOptions}
                  onChangeValue={(val) => handleItemChange(index, 'afterGTU', val)}
                  inputClassName="h-10 bg-white"
                />
                {!item.isNew && item.beforeGTU !== item.afterGTU && (
                  <div className="text-xs text-gray-500 mt-1">
                    {t("invoices:before_value")}: {item.beforeGTU}
                  </div>
                )}
              </div>

              {/* PKWIU - conditional */}
              {['8%', '5%', '0%'].includes(item.afterTypeOfVat) && (
                <div>
                  <label className="block text-sm font-medium pb-1">PKWIU</label>
                  <Input
                    value={item.afterPkwiu || ''}
                    onChange={(e) => handleItemChange(index, 'afterPkwiu', e.target.value)}
                    className="h-10 bg-white"
                  />
                  {!item.isNew && item.beforePkwiu !== item.afterPkwiu && (
                    <div className="text-xs text-gray-500 mt-1">
                      {t("invoices:before_value")}: {item.beforePkwiu || '-'}
                    </div>
                  )}
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium pb-1">
                  {t("invoices:positions_amount")}
                </label>
                <Input
                  type="number"
                  value={item.afterAmount}
                  onChange={(e) => handleItemChange(index, 'afterAmount', e.target.value)}
                  className="h-10 bg-white"
                  min="0"
                  step="1"
                />
                {!item.isNew && item.beforeAmount !== item.afterAmount && (
                  <div className="text-xs text-gray-500 mt-1">
                    {t("invoices:before_value")}: {item.beforeAmount}
                  </div>
                )}
              </div>

              {/* Unit */}
              <div>
                <SelectWithAlert
                  label={t("invoices:positions_unit")}
                  value={item.afterUnit}
                  options={unitOptions}
                  onChangeValue={(val) => handleItemChange(index, 'afterUnit', val)}
                  inputClassName="h-10 bg-white"
                />
                {!item.isNew && item.beforeUnit !== item.afterUnit && (
                  <div className="text-xs text-gray-500 mt-1">
                    {t("invoices:before_value")}: {item.beforeUnit}
                  </div>
                )}
              </div>

              {/* Netto */}
              <div>
                <label className="block text-sm font-medium pb-1">
                  {t("invoices:price_netto")}
                </label>
                <Input
                  type="number"
                  value={item.afterPriceNetto}
                  onChange={(e) => handleItemChange(index, 'afterPriceNetto', e.target.value)}
                  className="h-10 bg-white"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* VAT Type */}
              <div>
                <SelectWithAlert
                  label="VAT"
                  value={item.afterTypeOfVat}
                  options={vatOptions}
                  onChangeValue={(val) => handleItemChange(index, 'afterTypeOfVat', val)}
                  inputClassName="h-10 bg-white"
                />
                {!item.isNew && item.beforeTypeOfVat !== item.afterTypeOfVat && (
                  <div className="text-xs text-gray-500 mt-1">
                    {t("invoices:before_value")}: {item.beforeTypeOfVat}
                  </div>
                )}
              </div>
            </div>

            {/* Row 3: Summary - Before/After/Diff for Netto and Brutto */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t text-sm">
              <div className="flex gap-6">
                <div>
                  <span className="text-gray-500">{t("invoices:before_value")} {t("invoices:price_netto")}:</span>{' '}
                  <span className="font-medium">{formatNumber(item.beforePriceNetto * item.beforeAmount)}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t("invoices:after_value")}:</span>{' '}
                  <span className="font-medium">{formatNumber(item.afterPriceNetto * item.afterAmount)}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t("invoices:diff_value")}:</span>{' '}
                  <DiffCell value={item.diffPriceNetto} />
                </div>
              </div>
              <div className="flex gap-6">
                <div>
                  <span className="text-gray-500">{t("invoices:before_value")} {t("invoices:price_brutto")}:</span>{' '}
                  <span className="font-medium">{formatNumber(item.beforePriceBrutto * item.beforeAmount)}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t("invoices:after_value")}:</span>{' '}
                  <span className="font-medium">{formatNumber(item.afterPriceBrutto)}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t("invoices:diff_value")}:</span>{' '}
                  <DiffCell value={item.diffPriceBrutto} />
                </div>
              </div>

              {/* Delete button */}
              {item.isNew && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeItem(index)}
                  className="text-red-500 hover:text-red-700 ml-auto"
                >
                  <DeleteIcon fontSize="small" />
                  {t("common:button_delete")}
                </Button>
              )}
            </div>
          </div>
        ))}

        {correctedItems.length === 0 && (
          <div className="text-center text-gray-500 py-8 bg-white rounded-md border">
            {t('no_results')}
          </div>
        )}
      </div>
    </div>
  );
};
